/*
 * General Purpose Section including Signing up and updating credit card with Stripe
 */

import * as React from "react";
import { CardNumberElement } from "@stripe/react-stripe-js";
import { CardSection } from "./CardSection";
import { GFUser, GFPlan } from "../../models/models";
import { Button, Modal } from "react-bootstrap";
import CouponSection from "./CouponSection";
import constants from "../../constants";
import { toastr } from "react-redux-toastr";
import { hasStripeSubscription } from "../../reducers/userReducer";
import { throttle } from "lodash";
import { Stripe, StripeElements } from "@stripe/stripe-js";

interface PostAffTrackerInterface {
  setAccountId: (accountId: string) => void;
  track: () => void;
  createSale: () => any;
  register: () => void;
}

interface Props {
  user: GFUser;
  loading: boolean;
  displayAmount: string;
  hide: () => void;
  stripeCoupon: string;
  stripeCouponDetails: any;
  couponChange: (e: any) => void;
  checkStripeCoupon: (e: any) => void;
  paymentSignup: (
    paymentPlan: string,
    quantity: number,
    paymentToken: any,
    stripeCoupon: string
  ) => Promise<void>;
  manualAjaxEnd: any;
  manualAjaxStart: any;
  updateSubscription: (paymentPlan: string, quantity: number) => Promise<void>;
  selectedPlan: GFPlan;
  stripe: Stripe;
  elements: StripeElements;
  togglePlanRecurringType: () => void;
}
class PaymentSection extends React.Component<Props, {}> {
  // created a global variable to stop stripe payment from double submitting
  private handlePaymentThrottled: (event: React.MouseEvent<Button, MouseEvent>) => void;
  constructor(props: Props) {
    super(props);
    this.state = {};
    this.handlePaymentThrottled = throttle(
      this.handlePayment,
      constants.paymentDoubleClickTimeout
    );
  }

  handleHomeschoolTracking(user: any) {
    const couponCode = this.props.stripeCoupon || "";
    return new Promise((resolve) => {
      if (window && window.PostAffTracker) {
        // Only homeschool users on the annual plan get tracked
        if (user && user.id && user.paymentPlan === "GF-Tier2-Annual-5" && couponCode.toLowerCase() === "homeschool") {
          window.PostAffTracker.setAccountId('c04ca1f1');
          const sale = window.PostAffTracker.createSale();
          sale.setCoupon(couponCode.toLowerCase());
          sale.setTotalCost('39.99');
          sale.setOrderID(user.id);
          sale.setProductID('GF-Tier2-Annual-5');
          window.PostAffTracker.register();
          console.log("Homeschool Tracking success", sale);
          return resolve(true);
        }
        return resolve(false);
      }
      console.error("missing window.PostAffTracker");
      return resolve(false);
    });
  }

  handlePayment = (e: React.MouseEvent<Button, MouseEvent>) => {
    e.preventDefault();
    if (this.hasActiveStripePlan()) {
      // they have a subscription so lets change it
      this.handleUpdateSubscription();
    } else {
      this.handleSignup();
    }
  };
  handleSignup() {
    if (!this.props.stripe && this.props.elements) {
      console.error("missing stripe", this.props.stripe);
      toastr.error('Error', 'Missing stripe while trying to signup.  Please try again or contact support.', constants.toastrError)

      return;
    }
    const cardElement = this.props.elements.getElement(CardNumberElement);
    if (!cardElement) {
      console.error("missing cardElement");
      toastr.error('Error', 'Missing credit card while trying to signup.  Please try again or contact support.', constants.toastrError)
      return;
    }
    this.props.manualAjaxStart(); // have to call ajaxstart manually because we are caling stripe's API here
    this.props.stripe
      .createToken(cardElement)
      .then(({ token }) => {
        this.props.manualAjaxEnd();
        if (token === undefined) {
          throw {
            message:
              "Invalid credit card info.  Please double check and try again.",
            status: 433,
          };
        }
        return this.props
          .paymentSignup(
            this.props.selectedPlan.id,
            this.props.selectedPlan.quantity,
            token,
            this.props.stripeCoupon
          )
          .then((value) => {
            this.props.hide();
            this.handleHomeschoolTracking(value);
          });
      })
      .catch((error) => {
        // catch the error here because we are catching the stripe createToken error as well
        this.props.manualAjaxEnd();
        console.error("error signing up for a plan", error);
        let message = "";
        if (error && error.response) {
          if (error.response.status === 412) {
            message = constants.friendlyErrors.verifyRequired;
          }
          if (error.response.status === 433) {
            // this is an error we thew in the client app
            message = error.message;
          }
        }
        if (message.length) {
          toastr.error("Error", message, constants.toastrError);
        } else {
          constants.handleError(error, "signup");
        }
      });
  }
  handleUpdateSubscription() {
    this.props.updateSubscription(this.props.selectedPlan.id, this.props.selectedPlan.quantity).then((user) => {
      this.props.hide();
    });
  }

  /* decide if we should show the credit card form or a downgrade/upgrade button\
   Only show the downgrade/upgrade if they have an active subscription and have a stripeSubscriptionID
   */
  hasActiveStripePlan = () => {
    const { user } = this.props;
    return (
      hasStripeSubscription(this.props.user) === true &&
      user.stripeSubscriptionID &&
      user.stripeSubscriptionID.length > 0
    );
  };

  render() {
    return (
      <div>
        <form>
          <Modal.Body>
            {// checking to see if they already have a plan, if they do not, print this out
            this.hasActiveStripePlan() === false && (
              <div>
                <CardSection />
                {/*<CouponSection*/}
                {/*  stripeCoupon={this.props.stripeCoupon}*/}
                {/*  stripeCouponDetails={this.props.stripeCouponDetails}*/}
                {/*  checkStripeCoupon={this.props.checkStripeCoupon}*/}
                {/*  handleChange={this.props.couponChange}*/}
                {/*  loading={this.props.loading}*/}
                {/*/>*/}
              </div>
            )}
            {// If they do have plan, print out text
            this.hasActiveStripePlan() === true && (
              <div>
                <h3>
                  When upgrading or downgrading a subscription, your plan may
                  take on a new billing date depending upon your billing
                  interval. Changes in cost will either be prorated immediately
                  or in the following month’s invoice.
                </h3>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" onClick={this.props.hide}>
              Close
            </Button>
            <Button
              bsStyle="primary"
              type="button" // for some reason the submit button here does a form Post rather than calling the onSubmit function, so we will use a regular button type
              disabled={this.props.loading}
              onClick={this.handlePaymentThrottled}
            >
              {this.props.displayAmount}
            </Button>
          </Modal.Footer>
        </form>
      </div>
    );
  }
}

export default PaymentSection;

declare global {
  interface Window {
    PostAffTracker: PostAffTrackerInterface;
  }
}
